import { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Upload, ImageIcon } from 'lucide-react';

export function SettingsContentTab({ settings, update }) {
  const [uploadingLogo, setUploadingLogo] = useState(false);

  return (
    <div className="space-y-6">
      {/* Logo du restaurant */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-4">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Logo du restaurant</h3>
        <p className="text-xs text-charcoal/50">Uploadez le logo de votre restaurant. Il sera affiché dans la barre de navigation et le pied de page du site. Si aucun logo n'est défini, le nom du restaurant sera affiché en texte.</p>
        {settings.restaurant_logo_url ? (
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-xl border border-garden-200 bg-garden-50 flex items-center justify-center overflow-hidden">
              <img
                src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
                data-testid="admin-logo-preview"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="logo-upload-replace"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingLogo(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await adminApi.uploadLogo(fd);
                      update('restaurant_logo_url', res.data.url);
                      toast.success('Logo mis à jour');
                    } catch { toast.error('Erreur lors de l\'upload'); }
                    setUploadingLogo(false);
                  }}
                />
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-garden-100 text-garden-800 text-sm rounded-lg hover:bg-garden-200 transition-colors">
                  <Upload size={14} /> Remplacer
                </span>
              </label>
              <button
                onClick={async () => {
                  try {
                    await adminApi.deleteLogo();
                    update('restaurant_logo_url', '');
                    toast.success('Logo supprimé');
                  } catch { toast.error('Erreur'); }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 text-sm rounded-lg hover:bg-red-50 transition-colors"
                data-testid="logo-delete-btn"
              >
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </div>
        ) : (
          <label className="cursor-pointer flex flex-col items-center justify-center gap-2 border-2 border-dashed border-garden-300 rounded-xl py-8 hover:border-garden-500 hover:bg-garden-50/50 transition-colors" data-testid="logo-upload-zone">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="logo-upload-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingLogo(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  const res = await adminApi.uploadLogo(fd);
                  update('restaurant_logo_url', res.data.url);
                  toast.success('Logo uploadé');
                } catch { toast.error('Erreur lors de l\'upload'); }
                setUploadingLogo(false);
              }}
            />
            {uploadingLogo ? (
              <div className="w-8 h-8 border-2 border-garden-600 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <ImageIcon size={32} className="text-garden-400" />
                <span className="text-sm text-garden-600 font-medium">Cliquez pour uploader un logo</span>
                <span className="text-xs text-charcoal/40">PNG, JPG, SVG — 500x500px recommandé</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section Hero (accueil)</h3>
        <div>
          <Label className="text-sm">Titre principal</Label>
          <Input value={settings.hero_title || ''} onChange={e => update('hero_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Sous-titre</Label>
          <Textarea value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} className="border-garden-200 rounded-xl" rows={2} />
        </div>
      </div>

      {/* À propos */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section "Notre cuisine"</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.about_title || ''} onChange={e => update('about_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte (utilisez \n pour séparer les paragraphes)</Label>
          <Textarea value={settings.about_text || ''} onChange={e => update('about_text', e.target.value)} className="border-garden-200 rounded-xl" rows={4} />
        </div>
        <div>
          <Label className="text-sm">Tags (badges affichés sous "Notre cuisine")</Label>
          <p className="text-xs text-charcoal/50 mb-2">Ex : Burgers maison, Produits frais, Tapas, Formules dès X €</p>
          <div className="flex flex-wrap gap-2 mt-1 mb-3">
            {(settings.about_tags || []).map((tag) => (
              <span key={tag} className="bg-garden-100 text-garden-800 px-3 py-1 rounded-full text-sm flex items-center gap-1.5">
                {tag}
                <button onClick={() => update('about_tags', (settings.about_tags || []).filter(t => t !== tag))} className="text-garden-600 hover:text-red-500" data-testid={`remove-tag-${tag}`}><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Nouveau tag (ex: Burgers maison)"
              className="border-garden-200 rounded-xl"
              data-testid="new-tag-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = e.currentTarget.value.trim();
                  if (val && !(settings.about_tags || []).includes(val)) {
                    update('about_tags', [...(settings.about_tags || []), val]);
                    e.currentTarget.value = '';
                  }
                }
              }}
            />
            <button
              type="button"
              data-testid="add-tag-btn"
              className="bg-garden-800 text-white px-4 py-2 rounded-xl text-sm hover:bg-garden-900 transition-colors shrink-0"
              onClick={(e) => {
                const input = e.currentTarget.parentElement.querySelector('input');
                const val = input.value.trim();
                if (val && !(settings.about_tags || []).includes(val)) {
                  update('about_tags', [...(settings.about_tags || []), val]);
                  input.value = '';
                }
              }}
            >Ajouter</button>
          </div>
          <p className="text-[11px] text-charcoal/40 mt-1">Astuce : pour "Formules dès X €", saisissez le texte exact souhaité ; le minimum est calculé automatiquement dans la barre d'infos rapide.</p>
        </div>
      </div>

      {/* Horaires de service */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Horaires de service (affichage)</h3>
        <p className="text-xs text-charcoal/60">Ces horaires sont utilisés pour les mentions textuelles sur le site (ex: "Cuisine dès 11h30"). Pour modifier les créneaux réels de réservation, allez dans l'onglet "Horaires" du menu principal.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Ouverture Restaurant</Label>
            <Input value={settings.restaurant_opening_time || ''} onChange={e => update('restaurant_opening_time', e.target.value)} className="border-garden-200 rounded-xl" placeholder="9h30" />
          </div>
          <div>
            <Label className="text-sm">Ouverture Cuisine</Label>
            <Input value={settings.kitchen_opening_time || ''} onChange={e => update('kitchen_opening_time', e.target.value)} className="border-garden-200 rounded-xl" placeholder="11h30" />
          </div>
        </div>
      </div>

      {/* Avis Google (note + lien) */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Note Google & lien avis</h3>
        <p className="text-xs text-charcoal/60">Saisie manuelle assistée (pas de synchronisation automatique). À mettre à jour à partir de votre fiche Google Business / Google Maps.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm">Note /5</Label>
            <Input type="number" min="0" max="5" step="0.1" value={settings.google_rating || ''} onChange={e => update('google_rating', e.target.value)} className="border-garden-200 rounded-xl" placeholder="4.1" data-testid="google-rating-input" />
          </div>
          <div>
            <Label className="text-sm">Nombre d'avis</Label>
            <Input type="number" min="0" value={settings.google_reviews_count || ''} onChange={e => update('google_reviews_count', e.target.value)} className="border-garden-200 rounded-xl" placeholder="120" data-testid="google-reviews-count-input" />
          </div>
          <div>
            <Label className="text-sm">Date de mise à jour</Label>
            <Input type="date" value={settings.google_reviews_updated_at || ''} onChange={e => update('google_reviews_updated_at', e.target.value)} className="border-garden-200 rounded-xl" data-testid="google-reviews-updated-input" />
          </div>
        </div>
        <div>
          <Label className="text-sm">Lien vers la fiche Google (avis)</Label>
          <Input value={settings.google_reviews_url || ''} onChange={e => update('google_reviews_url', e.target.value)} className="border-garden-200 rounded-xl" placeholder="https://g.page/.../review" data-testid="google-reviews-url-input" />
          <p className="text-[11px] text-charcoal/40 mt-1">Si vide, le lien Google Maps (champ Adresse) est utilisé en repli.</p>
        </div>
      </div>

      {/* Atmosphère */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section "Atmosphère / Terrasse"</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.atmosphere_title || ''} onChange={e => update('atmosphere_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte</Label>
          <Textarea value={settings.atmosphere_text || ''} onChange={e => update('atmosphere_text', e.target.value)} className="border-garden-200 rounded-xl" rows={4} />
        </div>
      </div>

      {/* Boutons */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Textes des boutons</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Bouton Réserver</Label>
            <Input value={settings.btn_reserve || ''} onChange={e => update('btn_reserve', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Réserver une table" />
          </div>
          <div>
            <Label className="text-sm">Bouton Carte</Label>
            <Input value={settings.btn_menu || ''} onChange={e => update('btn_menu', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Voir la carte" />
          </div>
          <div>
            <Label className="text-sm">Bouton Commander</Label>
            <Input value={settings.btn_order || ''} onChange={e => update('btn_order', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Commander" />
          </div>
          <div>
            <Label className="text-sm">Bouton Appeler</Label>
            <Input value={settings.btn_call || ''} onChange={e => update('btn_call', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Appeler" />
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Section finale (CTA)</h3>
        <div>
          <Label className="text-sm">Titre</Label>
          <Input value={settings.cta_title || ''} onChange={e => update('cta_title', e.target.value)} className="border-garden-200 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm">Texte</Label>
          <Textarea value={settings.cta_text || ''} onChange={e => update('cta_text', e.target.value)} className="border-garden-200 rounded-xl" rows={2} />
        </div>
      </div>

      {/* Bloc Formules (badge + texte global Fait Maison) */}
      <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
        <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Bloc "Nos Formules" (page d'accueil)</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Titre de la section</Label>
            <Input value={settings.formules_section_title || ''} onChange={e => update('formules_section_title', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Nos Formules" data-testid="formules-section-title-input" />
          </div>
          <div>
            <Label className="text-sm">Sous-titre</Label>
            <Input value={settings.formules_section_subtitle || ''} onChange={e => update('formules_section_subtitle', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Cuisine maison, produits frais du marché" data-testid="formules-section-subtitle-input" />
          </div>
        </div>
        <div>
          <Label className="text-sm">Libellé du badge sur la formule mise en avant</Label>
          <Input value={settings.formule_discovery_badge || ''} onChange={e => update('formule_discovery_badge', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Formule Decouverte" data-testid="formule-discovery-badge-input" />
          <p className="text-[11px] text-charcoal/40 mt-1">Texte affiché sur la formule la moins chère / Plat du jour. Défaut : "Formule Decouverte".</p>
        </div>
        <div className="border-t border-garden-100 pt-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm font-medium">Afficher le bandeau "Fait Maison"</Label>
            <button
              type="button"
              role="switch"
              aria-checked={settings.formules_fait_maison_enabled !== false}
              onClick={() => update('formules_fait_maison_enabled', settings.formules_fait_maison_enabled === false)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.formules_fait_maison_enabled !== false ? 'bg-garden-700' : 'bg-charcoal/20'}`}
              data-testid="fait-maison-enabled-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.formules_fait_maison_enabled !== false ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Libellé du badge</Label>
              <Input value={settings.formules_fait_maison_label || ''} onChange={e => update('formules_fait_maison_label', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Fait Maison" data-testid="fait-maison-label-input" />
            </div>
            <div>
              <Label className="text-sm">Texte d'accompagnement</Label>
              <Input value={settings.formules_fait_maison_text || ''} onChange={e => update('formules_fait_maison_text', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Nems, terrines et préparations…" data-testid="fait-maison-text-input" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Pied de page (Footer)</h3>
          <div>
            <Label className="text-sm">Description du restaurant</Label>
            <Textarea value={settings.footer_description || ''} onChange={e => update('footer_description', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="Restaurant convivial à Bordeaux..." data-testid="footer-description-input" />
            <p className="text-xs text-charcoal/50 mt-1">Texte affiché dans le pied de page. Si vide, utilise la traduction par défaut.</p>
          </div>
          <div className="flex items-center justify-between p-4 border border-garden-200 rounded-xl">
            <div>
              <Label className="text-sm font-medium">Afficher le numéro de version</Label>
              <p className="text-xs text-charcoal/50">Masque le V-commit dans le pied de page public.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.footer_show_version === true}
              onClick={() => update('footer_show_version', settings.footer_show_version !== true)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.footer_show_version === true ? 'bg-garden-700' : 'bg-charcoal/20'}`}
              data-testid="footer-show-version-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.footer_show_version === true ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
          <div>
            <Label className="text-sm">Informations d'accès (transpor t/s)</Label>
            <Textarea value={settings.access_info || ''} onChange={e => update('access_info', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="Tram A (arrêt à proximité) / Bus 9 (arrêt à proximité)" data-testid="footer-access-info-input" />
            <p className="text-xs text-charcoal/50 mt-1">Texte affiché dans le pied de page pour les transports.</p>
          </div>

          <div>
            <Label className="text-sm">Liens du pied de page</Label>
            <p className="text-xs text-charcoal/50 mb-3">Liens de navigation personnalisés affichés dans le pied de page. Si vide, les liens par défaut sont utilisés (accueil, carte, galerie, avis, etc.).</p>
            <div className="space-y-2 mb-3">
              {(Array.isArray(settings.footer_links) ? settings.footer_links : []).map((link, idx) => {
                const label = typeof link === 'string' ? link : (link?.label || link?.href || '');
                const href = typeof link === 'string' ? '' : (link?.href || '');
                const displayLabel = typeof link === 'string' ? link : (link?.label || '');
                return (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      value={displayLabel || ''}
                      onChange={e => {
                        const links = Array.isArray(settings.footer_links) ? settings.footer_links : [];
                        const newLinks = [...links];
                        newLinks[idx] = { label: e.target.value, href: href || '' };
                        update('footer_links', newLinks);
                      }}
                      className="border-garden-200 rounded-xl flex-1"
                      placeholder="Libellé"
                      data-testid={`footer-link-label-${idx}`}
                    />
                    <Input
                      value={href || ''}
                      onChange={e => {
                        const links = Array.isArray(settings.footer_links) ? settings.footer_links : [];
                        const newLinks = [...links];
                        newLinks[idx] = { label: displayLabel || '', href: e.target.value };
                        update('footer_links', newLinks);
                      }}
                      className="border-garden-200 rounded-xl flex-1"
                      placeholder="/chemin"
                      data-testid={`footer-link-href-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const links = Array.isArray(settings.footer_links) ? settings.footer_links : [];
                        update('footer_links', links.filter((_, i) => i !== idx));
                      }}
                      className="text-red-600 hover:text-red-800 p-1"
                      data-testid={`footer-link-remove-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Nouveau lien (libellé)"
                className="border-garden-200 rounded-xl flex-1"
                data-testid="new-footer-link-label"
                id="new-footer-link-label"
              />
              <Input
                placeholder="/chemin"
                className="border-garden-200 rounded-xl flex-1"
                data-testid="new-footer-link-href"
                id="new-footer-link-href"
              />
              <button
                type="button"
                data-testid="add-footer-link-btn"
                className="bg-garden-800 text-white px-4 py-2 rounded-xl text-sm hover:bg-garden-900 transition-colors shrink-0"
                onClick={() => {
                  const labelInput = document.getElementById('new-footer-link-label');
                  const hrefInput = document.getElementById('new-footer-link-href');
                  const label = labelInput?.value.trim() || '';
                  const href = hrefInput?.value.trim() || '';
                  if (label && href) {
                    const links = Array.isArray(settings.footer_links) ? settings.footer_links : [];
                    update('footer_links', [...links, { label, href }]);
                    labelInput.value = '';
                    hrefInput.value = '';
                  }
                }}
              >Ajouter</button>
          </div>
          </div>
        </div>

        {/* Mentions légales dynamiques */}
        <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Mentions légales dynamiques</h3>
          <p className="text-xs text-charcoal/50">Personnalisez les textes des sections "Cookies", "Politique de confidentialité" et "Responsabilité". Si un champ est vide, la traduction par défaut est utilisée.</p>
          <div>
            <Label className="text-sm">Texte Cookies</Label>
            <Textarea value={settings.legal_cookies_text || ''} onChange={e => update('legal_cookies_text', e.target.value)} className="border-garden-200 rounded-xl" rows={3} data-testid="legal-cookies-input" />
          </div>
          <div>
            <Label className="text-sm">Texte Politique de confidentialité</Label>
            <Textarea value={settings.legal_privacy_text || ''} onChange={e => update('legal_privacy_text', e.target.value)} className="border-garden-200 rounded-xl" rows={3} data-testid="legal-privacy-input" />
          </div>
          <div>
            <Label className="text-sm">Texte Responsabilité</Label>
            <Textarea value={settings.legal_responsibility_text || ''} onChange={e => update('legal_responsibility_text', e.target.value)} className="border-garden-200 rounded-xl" rows={3} data-testid="legal-responsibility-input" />
          </div>
        </div>

        {/* Galerie */}
        <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Galerie</h3>
          <div>
            <Label className="text-sm">Titre de la galerie</Label>
            <Input value={settings.gallery_title || ''} onChange={e => update('gallery_title', e.target.value)} className="border-garden-200 rounded-xl" placeholder="En images" data-testid="gallery-title-input" />
          </div>
          <div>
            <Label className="text-sm">Description</Label>
            <Textarea value={settings.gallery_description || ''} onChange={e => update('gallery_description', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="Découvrez notre terrasse, notre salle et nos plats en photos." data-testid="gallery-description-input" />
          </div>
          <div>
            <Label className="text-sm">Catégories de filtres (séparées par des virgules)</Label>
            <Input
              value={Array.isArray(settings.gallery_filter_categories) ? settings.gallery_filter_categories.join(', ') : (settings.gallery_filter_categories || '').toString()}
              onChange={e => update('gallery_filter_categories', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="border-garden-200 rounded-xl"
              placeholder="Plats, Terrasse, Salle, Ambiance"
              data-testid="gallery-filter-categories-input"
            />
            <p className="text-xs text-charcoal/50 mt-1">Si vide, les catégories par défaut sont utilisées.</p>
          </div>
        </div>

        {/* Carte */}
        <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Carte</h3>
          <div>
            <Label className="text-sm">Texte d'introduction de la carte</Label>
            <Textarea value={settings.menu_intro_text || ''} onChange={e => update('menu_intro_text', e.target.value)} className="border-garden-200 rounded-xl" rows={3} placeholder="Découvrez nos burgers maison, tapas à partager..." data-testid="menu-intro-text-input" />
            <p className="text-xs text-charcoal/50 mt-1">Texte affiché sous le titre de la page Carte. Si vide, utilise la traduction par défaut.</p>
          </div>
          <div>
            <Label className="text-sm">Texte "Accompagnements compris"</Label>
            <Input value={settings.menu_sides_included_text || ''} onChange={e => update('menu_sides_included_text', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Frites fraîches ou Salade de saison." data-testid="menu-sides-included-input" />
          </div>
          <div>
            <Label className="text-sm">Texte "Suppléments"</Label>
            <Input value={settings.menu_sides_supplements_text || ''} onChange={e => update('menu_sides_supplements_text', e.target.value)} className="border-garden-200 rounded-xl" placeholder="Frites de Patate douce +3€ | Bacon +3€ | Cheddar +2€" data-testid="menu-sides-supplements-input" />
          </div>
          <div>
            <Label className="text-sm">Note de bas de carte (origine viande, etc.)</Label>
            <Textarea value={settings.menu_sides_note || ''} onChange={e => update('menu_sides_note', e.target.value)} className="border-garden-200 rounded-xl" rows={2} placeholder="Steack ANGUS 100% Pur Boeuf Origine France..." data-testid="menu-sides-note-input" />
          </div>
        </div>

        {/* Avis */}
        <div className="bg-white rounded-xl border border-garden-200 p-6 space-y-5">
          <h3 className="font-heading font-semibold text-garden-800 border-b border-garden-100 pb-2">Avis</h3>
          <div>
            <Label className="text-sm">Texte d'introduction de la page Avis</Label>
            <Textarea value={settings.reviews_intro_text || ''} onChange={e => update('reviews_intro_text', e.target.value)} className="border-garden-200 rounded-xl" rows={3} placeholder="Majorité d'avis 4 et 5 étoiles..." data-testid="reviews-intro-text-input" />
            <p className="text-xs text-charcoal/50 mt-1">Texte affiché sous le titre de la page Avis. Si vide, utilise la traduction par défaut.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
